import React, { useRef, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTrigger,
} from "@/components/ui/dialog";
import FeedbackIcon from "@/assets/images/FEEDBACK-ICON.svg";
import Image from "next/image";
import Logo from "@/assets/images/logo.png";
import { validationRules } from "@/lib/validationRules";
import { useToast } from "@/components/ui/use-toast";
import { Controller, useForm } from "react-hook-form";
import emailjs from "emailjs-com";
import Feedback from "@/assets/images/feedback.png";
import { Textarea } from "@/components/ui/textarea";
import { useContactUsMutation } from "@/redux/apiSlices/formApi";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const feedbackOptions = ["Website Feedback", "Store Feedback", "Associate Feedback"];
const ratingOptions = ["Terrible", "Poor", "Average", "Good", "Excellent"];

const FeedbackStep0 = ({ onContinue }) => {
  const [selectedOption, setSelectedOption] = useState(null);

  return (
    <div>
      <h4 className="text-[14px] leading-[20px] mb-[22px]">
        We appreciate your feedback. Your opinions will help us provide you the best shopping experience - in store and online.
      </h4>
      <h4 className="text-[14px] leading-[20px] mb-[17px] font-semibold">
        What type of feedback would you like to provide?
      </h4>
      <div className="mb-[46px] flex gap-[4px] flex-wrap gap-y-2">
        {feedbackOptions.map((option) => (
          <Button
            key={option}
            variant={selectedOption === option ? "default" : "outline"}
            className={`text-[12px] leading-[18px] px-4 py-[9px] rounded-[4px] border ${
              selectedOption === option ? "bg-[#0B223C] text-white border-[#0B223C]" : "text-[#424242] border-[#DBDBDB]"
            }`}
            onClick={() => setSelectedOption(option)}
          >
            {option}
          </Button>
        ))}
      </div>
      <Button
        onClick={onContinue}
        disabled={!selectedOption} // Disable until an option is selected
        className={`w-[126px] rounded-[4px] h-[46px] ${
          selectedOption ? "bg-[#0B223C]" : "bg-gray-400 cursor-not-allowed"
        }`}
      >
        Continue
      </Button>
    </div>
  );
};

const FeedbackStep1 = ({ formRef, onSubmit, handleSubmit, control }) => {
  const [selectedRating, setSelectedRating] = useState(null);
  const technicalOption = [
    { label: "Technical Issue", value: "Technical Issue" },
  ];

  return (
    <div>
      <h4 className="text-[13px] leading-[19px] text-[#252525] mb-[22px]">
        We appreciate your feedback. Your opinions will help us provide you the best shopping experience - in store and online.
      </h4>
      <div className="mb-[26px]">
        <h4 className="text-[13px] leading-[19px] text-[#252525] mb-[12px] font-semibold">
          * Rate your overall experience with this page.
        </h4>
        <div className="flex gap-[12px] flex-wrap gap-y-2">
          {ratingOptions.map((rating) => (
            <Button
              key={rating}
              variant={selectedRating === rating ? "default" : "outline"}
              className={`text-[12px] leading-[18px] px-4 py-[9px] rounded-[4px] border h-[36px] ${
                selectedRating === rating ? "bg-[#0B223C] text-white border-[#0B223C]" : "text-[#424242] border-[#DBDBDB]"
              }`}
              onClick={() => setSelectedRating(rating)}
            >
              {rating}
            </Button>
          ))}
        </div>
      </div>
      <form ref={formRef} onSubmit={handleSubmit(onSubmit)}>
        <FeedbackDropdown label="Please select your feedback topic" placeholder="Technical Issue" />      
        <TextareaSection label="Please share your feedback." control={control} />
        <FeedbackDropdown label="What is your primary purpose for today's visit?" placeholder="Make a purchase" />
        <FeedbackDropdown label="Were you able to complete your primary purpose for today's visit?" placeholder="Yes" options={["Yes", "No"]} />
        <Button 
          type="submit" 
          disabled={!selectedRating} // Disable until a rating is selected
          className={`w-[126px] rounded-[4px] h-[46px] ${
            selectedRating ? "bg-[#0B223C]" : "bg-gray-400 cursor-not-allowed"
          }`}
        >
          Send Feedback
        </Button>
      </form>
    </div>
  );
};


const FeedbackStep2 = ({ onOk }) => (
  <div className="flex flex-col justify-start items-center gap-[22px]">
    <Image src={Feedback} alt="Feedback" width={80} height={80} />
    <div className="text-center">
      <h4 className="font-semibold text-[15px] leading-[22px] text-[#252525] mb-[10px]">Thank you for your feedback!</h4>
      <p className="max-w-[400px] text-[12px] leading-[17px] text-[#252525]">
        We appreciate your feedback. Your opinions will help us provide you the best shopping experience - in store and online.
      </p>
    </div>
    <Button onClick={onOk} className="w-[126px] rounded-[4px] h-[46px] bg-[#0B223C]">Ok</Button>
  </div>
);

const FeedbackDropdown = ({ label, placeholder, options = ["Technical Issue"] }) => (
  <div className="mb-[26px]">
    <h4 className="text-[13px] leading-[19px] text-[#252525] mb-[12px] font-semibold">{label}</h4>
    <Select>
      <SelectTrigger className="w-[180px] rounded-[10px] h-11 mb-[27px]">
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        <SelectGroup>
          <SelectLabel>{placeholder}</SelectLabel>
          {options.map(option => (
            <SelectItem key={option} value={option}>{option}</SelectItem>
          ))}
        </SelectGroup>
      </SelectContent>
    </Select>
  </div>
);

const TextareaSection = ({ label, control }) => (
  <div className="mb-[26px]">
    <h4 className="text-[13px] leading-[19px] text-[#252525] mb-[12px] font-semibold">{label}</h4>    
    <Controller
      name="message"
      control={control}
      defaultValue=""
      render={({ field }) => (
        <Textarea {...field} placeholder="Message Here" className="mt-[10px]" />
      )}
    />
  </div>
);

const FeedBack = () => {
  const [isShowFeedback, setIsShowFeedback] = useState(0);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { toast } = useToast();
  const formRef = useRef(null);

  const { control, handleSubmit, reset } = useForm({ defaultValues: {} });

  const [contactUs, { data, isSuccess, isLoading: isUserLoading, isError, error: errMsg }] = useContactUsMutation();
  useEffect(() => {
    if (isSuccess) {
      if (data?.success) {
        toast({ title: "The email has been sent successfully." })
      }
    }
    if (isError) {
      toast({
        title: errMsg?.data?.msg,
      });
    }
  }, [isSuccess, isError]);

  const onSubmit = async (data) => {
    if (!formRef.current) return;

    try {
      const subject = `Feedback`;
      const formType=`Feedback`;
      const formData = { ...data, subject, formType };
    
      await contactUs(formData);
      reset();
      setIsShowFeedback(2);
    } catch (error) {
      console.error("❌ Error sending email:", error);
      toast({ title: "Failed to send email. Please try again later." });
    }
  };

  return (
    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}  >
      <DialogTrigger asChild>
        <Button 
         className="smd:mt-3 py-[11px] px-[19px] border border-blue2 hover:bg-inherit text-blue2  bg-transparent h-[54px] text-[15px] inline-flex items-center gap-3 rounded-[6px]"
         onClick={() => setIsDialogOpen(true)}>
          <Image src={FeedbackIcon} alt="icon" />
          Feedback
        </Button>
      </DialogTrigger>
      <DialogContent  className="max-h-[calc(100%-44px)] overflow-auto" >
        <div>
        <DialogHeader>
          <DialogTitle>Feedback</DialogTitle> {/* Added for accessibility */}
          <Image src={Logo} alt="icon" className="max-w-[116px]" />
        </DialogHeader>
        {isShowFeedback === 0 && <FeedbackStep0 onContinue={() => setIsShowFeedback(1)} />}
        {isShowFeedback === 1 && <FeedbackStep1 formRef={formRef} onSubmit={onSubmit} handleSubmit={handleSubmit} control={control} />}
        {isShowFeedback === 2 && <FeedbackStep2 onOk={() => setIsDialogOpen(false)} />}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FeedBack;
